import { addBreadcrumb, captureError } from "./sentry";

/**
 * #621 — the single reporting path for a caregiver-visible client-profile panel
 * failure. Two rules live here so three panels cannot drift apart.
 *
 * 1. NEVER send the caught error itself. `parseJsonOrThrow` builds `err.message`
 *    from the RESPONSE BODY (`body.message || body.error`), and
 *    `src/api/responseUtils.js` is explicit: "arbitrary backend text and may
 *    carry alert content … Report `err.status`, never `err.message`." In a care
 *    product that text can be PHI. We construct our own error with a fixed
 *    message and attach only fields that are ours: status, error NAME (the
 *    class, e.g. "TypeError" — not the body), and the client id.
 *
 * 2. Do not double-report. `baseApi.reportApiFailure` already captures network
 *    failures and HTTP 5xx. Reporting those again here emits two events for one
 *    failed request. It does NOT cover 4xx, which is why this exists at all.
 *
 * Returns true when an event was sent, so callers and tests can assert either
 * direction.
 */

// Thrown verbatim by baseApi after it has already reported the failure.
// Re-exported so the producer/consumer contract has one name and the tests can
// assert both ends of it. The string itself is baseApi's -- see the comment on
// API_NETWORK_MESSAGE for why this may not be a fourth copy.
export { API_NETWORK_MESSAGE as API_NETWORK_SENTINEL } from "../api/baseApi";

/**
 * #621's stated acceptance criterion, which the first version of this work
 * missed: "Acceptance has to come from a reproducing probe plus the presence of
 * a success breadcrumb, not from a decline in errors." With ~2 events/day on
 * this project a falling error count is not falsifiable, so the issue asks for
 * BOTH paths breadcrumbed under `client_profile.<surface>.load_{succeeded,failed}`.
 *
 * `data` carries the client id and the HTTP status only — never body text, for
 * the same reason capturePanelFailure constructs its own error.
 */
/**
 * #683 — panel ops that are an initial/refresh LOAD (not a post-load action).
 * `capturePanelFailure` uses this so assign/remove/toggle/launcher failures do
 * not contaminate `client_profile.<surface>.load_failed` monitors.
 * Watch vitals load paths use `op: "rejected"` / `"timeout"` (not `load-*`).
 */
export const isPanelLoadOp = (op) =>
  op === undefined ||
  op === null ||
  op === "" ||
  op === "load" ||
  op === "rejected" ||
  op === "timeout" ||
  String(op).startsWith("load-");

/**
 * Breadcrumb for a panel load OR a post-load action. Kind is `load` (default,
 * #621) or `action` (#683).
 */
export const panelSurfaceBreadcrumb = (
  surfaceKey,
  ok,
  { clientId, status, rowCount, kind = "load" } = {},
) => {
  const phase = kind === "action" ? "action" : "load";
  const cat = `client_profile.${surfaceKey}.${phase}_${ok ? "succeeded" : "failed"}`;
  addBreadcrumb(cat, {
    category: cat,
    level: ok ? "info" : "warning",
    clientId,
    // #621, verbatim: "Failure carries the HTTP status, or `network` when there
    // is none (a `Failed to fetch` has no status). Success carries the row
    // count, so 'loaded' and 'silently loaded nothing' are distinguishable."
    ...(ok
      ? { rowCount: typeof rowCount === "number" ? rowCount : null }
      : { status: typeof status === "number" ? status : "network" }),
  });
};

/** #621 load path — thin wrapper so existing call sites stay unchanged. */
export const panelLoadBreadcrumb = (surfaceKey, ok, data = {}) =>
  panelSurfaceBreadcrumb(surfaceKey, ok, { ...data, kind: "load" });

export const capturePanelFailure = (
  error,
  { panel, surfaceKey, surface = "client-profile", op, clientId } = {},
) => {
  const status = typeof error?.status === "number" ? error.status : null;

  // ORDERING, corrected under review. An earlier version breadcrumbed EVERY
  // failure here, on the reasoning that a 5xx would otherwise be invisible on
  // this panel's filter. That reasoning was wrong: for network/5xx, baseApi has
  // ALREADY called captureException by the time the service parses the response
  // and reaches us, so a breadcrumb added now cannot accompany that event — it
  // sits on the scope and rides the NEXT, unrelated one. `baseApi.js` documents
  // exactly this ("transmitted with the NEXT, unrelated event ... recorded as a
  // transport failure on someone else's error").
  //
  // A misattributed breadcrumb is worse than an absent one, so we breadcrumb
  // only the failures we also capture — 4xx — where both land in this same
  // synchronous block and belong to each other. Network/5xx remain visible via
  // baseApi's own event (tagged api_path / api_method), NOT on the panel
  // filter. Making them one coherent event needs panel context passed into the
  // central reporter; filed separately rather than half-done here.
  if (status !== null && status >= 500) return false;
  // A FLAG, not message equality. Comparing text made the dedupe depend on two
  // copies of a user-facing sentence staying byte-identical forever; rewording
  // it in baseApi would have started a second event here, silently (review
  // finding). `makeNetworkError` marks it, exactly as auth failures are marked.
  if (error?.isNetworkFailure) return false;
  // The THIRD central reporter, and the one this helper originally missed.
  // `acquireTokenOrReport` already calls `reportTokenFailure` and rethrows a
  // STATUSLESS error marked `isAuthFailure`. The reactive 401 path now uses
  // the same public error shape: baseApi privately marks the fallback Response
  // and responseUtils translates that internal marker to `isAuthFailure`.
  // Without this
  // skip each panel reports that failure a second time AND, having no numeric
  // status and not matching the network sentinel, labels its breadcrumb
  // `network` -- contradicting the `api.request.token_failed` diagnostics the
  // same request already emitted. baseApi's own docstring records this exact
  // bug being fixed once for the calendar; these panels reintroduced it.
  // Dedupe on the MARKER, never on the bare status. Review caught the first
  // version doing the latter, and it silently dropped a real failure: 401 ->
  // refresh SUCCEEDS -> retry -> 401 again never reaches reportTokenFailure, so
  // nothing captured it and skipping every 401 lost a caregiver-visible error
  // entirely. Losing a real failure is strictly worse than reporting a session
  // expiry, so the narrow test is the safe one.
  if (error?.isAuthFailure) return false;

  if (surfaceKey) {
    // #683: post-load actions must not emit load_failed.
    panelSurfaceBreadcrumb(surfaceKey, false, {
      clientId,
      status,
      kind: isPanelLoadOp(op) ? "load" : "action",
    });
  }

  // The op belongs in the TITLE, not only in a tag: "pontosense-staff load
  // failed" is simply untrue of an assign, remove, toggle or launcher failure,
  // and the title is the primary thing a triager reads in Sentry. `op` is our
  // own internal vocabulary -- never caller text -- so it is safe to interpolate.
  captureError(new Error(`${panel} failed (${op || "load"})`), {
    tags: {
      panel,
      surface,
      ...(op ? { op } : {}),
      ...(status !== null ? { status: String(status) } : {}),
    },
    extra: { clientId, status, errorName: error?.name },
  });
  return true;
};

